import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Box,
    Typography,
    Button,
    Alert,
    Chip,
    Tooltip,
    TextField,
    Avatar,
    useTheme,
    useMediaQuery,
    Card,
    CardContent,
    CardActions,
    CircularProgress,
    Backdrop,
    LinearProgress,
    Divider,
    Switch,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Badge,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import {
    Add,
    Business,
    Category,
    Image as ImageIcon,
    ContentCopy,
    FileDownload,
    Edit,
    Delete,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    FilterList as FilterListIcon,
    Clear as ClearIcon,
    QrCodeScanner as QrCodeScannerIcon,
    LocalOffer as SaleIcon,
    Campaign as CampaignIcon,
} from "@mui/icons-material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useSearchParams } from "react-router-dom";
import { BarcodeScannerDialog } from "../../components/common/BarcodeScannerDialog";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import { ServerSideDataTable } from "../../components/common/DataTable";
import { ProductForm } from "../../components/forms/ProductForm";
import { ProductDetailDrawer } from "../../components/products/ProductDetailDrawer";
import { ProductSearchBox } from "../../components/products/ProductSearchBox";
import {
    ProductImageButton,
    ProductImageViewer,
    ProductImageViewerTarget,
} from "../../components/products/ProductImageViewer";
import { QuickBarcodeDialog } from "../../components/products/QuickBarcodeDialog";
import { ProductAdvancedSearch } from "../../components/products/ProductAdvancedSearch";
import { AddToCampaignDialog } from "../../components/products/AddToCampaignDialog";
import { ComboChip } from "../../components/products/ComboChip";
import { productService } from "../../services/productService";
import { formatCurrencyBDT } from "../../utils/currency";
import { Product, PaginatedResponse, GetProductsParams } from "../../types";
import { ProductImage } from "../../types/productImage";
import { useColumnVisibility } from "../../hooks/useColumnVisibility";
import { usePermissions } from "../../hooks/usePermissions";
import { countActiveProductFilters } from "../../utils/productSearchParams";

export const ProductList: React.FC = () => {
    const { canWrite } = usePermissions();
    const [searchParams, setSearchParams] = useSearchParams();
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>("");
    const [formOpen, setFormOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(
        null,
    );
    const [formMode, setFormMode] = useState<"create" | "edit">("create");
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 50,
    });
    const [totalRows, setTotalRows] = useState(0);

    // Advanced Search filter state — the single source of truth for every
    // filter beyond the free-text search box (category, brand, price/stock
    // ranges, active/visible/featured, date added, sort). The category
    // chip below is a thin display layer over filters.category_id, not a
    // parallel filter of its own — this is what lets a category deep-link
    // from CategoryList, a manual category pick in Advanced Search, and
    // Clear All all agree on one piece of state instead of drifting.
    const [filters, setFilters] = useState<GetProductsParams>(() => {
        const id = searchParams.get("category_id");
        return id ? { category_id: id } : {};
    });
    // Just the resolved display name for the category chip — kept separate
    // from `filters` since it's presentation-only.
    const [categoryChipName, setCategoryChipName] = useState<string>("");
    const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);

    // The search applied to the list. What's being typed lives in
    // ProductSearchBox, so typing doesn't re-render this page and its grid.
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [barcodeScannerOpen, setBarcodeScannerOpen] = useState(false);
    // Loading a full product (rows are lean) before opening the form.
    const [busy, setBusy] = useState(false);

    // Column visibility state
    const { columnVisibility } = useColumnVisibility("products");
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // Export state
    const [exporting, setExporting] = useState(false);

    // Full-size image viewer (opened from a thumbnail)
    const [imageViewer, setImageViewer] = useState<ProductImageViewerTarget | null>(null);
    const openImages = useCallback(
        (product: Product) =>
            setImageViewer({
                productId: product.product_id,
                productName: product.name,
                images: product.images ?? [],
            }),
        [],
    );

    // Detail drawer state
    const [drawerProduct, setDrawerProduct] = useState<Product | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    // Quick barcode dialog state
    const [barcodeDialogProduct, setBarcodeDialogProduct] =
        useState<Product | null>(null);
    const [barcodeDialogOpen, setBarcodeDialogOpen] = useState(false);

    // Inline visibility toggle state
    const [visibilityDialog, setVisibilityDialog] = useState<{
        open: boolean;
        productId: string;
        productName: string;
        currentValue: boolean;
    }>({ open: false, productId: "", productName: "", currentValue: false });
    const [togglingVisibility, setTogglingVisibility] = useState<string | null>(null);

    // Bulk operations state
    const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>({ type: "include", ids: new Set() });
    const [bulkDialog, setBulkDialog] = useState<{
        open: boolean;
        action: "show" | "hide" | "remove-discount";
    }>({ open: false, action: "show" });
    const [bulkLoading, setBulkLoading] = useState(false);

    // Bulk "Apply Discount" — separate from bulkDialog above since it needs
    // its own form (percent-vs-flat, value, optional date window) and a
    // live preview, not just a plain confirm.
    const [discountDialog, setDiscountDialog] = useState(false);
    const [campaignDialog, setCampaignDialog] = useState(false);
    const [discountMode, setDiscountMode] = useState<"percent" | "flat">("percent");
    const [discountValue, setDiscountValue] = useState<number>(0);
    const [discountStartsAt, setDiscountStartsAt] = useState<Date | null>(null);
    const [discountEndsAt, setDiscountEndsAt] = useState<Date | null>(null);

    // One loading path for the list: page, page size, applied search,
    // filters, or an explicit refresh. Only the newest request may update
    // the grid, so a slow older response can never overwrite newer results.
    // Rows are lean (no descriptions) — see openFormWithFullProduct.
    const [reloadKey, setReloadKey] = useState(0);
    const refreshProducts = useCallback(() => setReloadKey((k) => k + 1), []);
    const requestId = useRef(0);
    useEffect(() => {
        const id = ++requestId.current;
        setLoading(true);
        productService
            .getProducts({
                skip: paginationModel.page * paginationModel.pageSize,
                limit: paginationModel.pageSize,
                ...(searchTerm && { search: searchTerm }),
                ...filters,
                lean: true,
            })
            .then((response: PaginatedResponse<Product>) => {
                if (id !== requestId.current) return;
                setProducts((response as any).data);
                setTotalRows(response.total);
            })
            .catch((err: any) => {
                if (id !== requestId.current) return;
                setError("Failed to load products");
                console.error("Products fetch error:", err);
            })
            .finally(() => {
                if (id === requestId.current) setLoading(false);
            });
    }, [paginationModel.page, paginationModel.pageSize, searchTerm, filters, reloadKey]);

    const handlePaginationModelChange = useCallback(
        (model: { page: number; pageSize: number }) => setPaginationModel(model),
        [],
    );

    // A new search starts again from the first page.
    const handleSearch = useCallback((term: string) => {
        setSearchTerm(term);
        setPaginationModel((m) => (m.page === 0 ? m : { ...m, page: 0 }));
    }, []);

    const handleExport = useCallback(async () => {
        setExporting(true);
        try {
            const blob = await productService.exportProducts({
                search: searchTerm || undefined,
                category_id: filters.category_id,
                brand_id: filters.brand_id,
                is_active: filters.is_active,
                is_visible: filters.is_visible,
                is_featured: filters.is_featured,
                min_price: filters.min_price,
                max_price: filters.max_price,
                min_cost_price: filters.min_cost_price,
                max_cost_price: filters.max_cost_price,
                stock_status: filters.stock_status,
                created_from: filters.created_from,
                created_to: filters.created_to,
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement("a");
            link.href = url;
            const dateStr = new Date().toISOString().split("T")[0];
            link.setAttribute("download", `products_export_${dateStr}.csv`);
            document.body.appendChild(link);
            link.click();
            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
            window.URL.revokeObjectURL(url);
        } catch (err: any) {
            console.error("Failed to export products:", err);
            setError("Failed to export products");
        } finally {
            setExporting(false);
        }
    }, [searchTerm, filters]);

    // Applies a new set of Advanced Search filters and goes back to page 1,
    // since the result set has just changed shape. The list loader reacts.
    const handleAdvancedSearch = useCallback(
        (newFilters: GetProductsParams) => {
            setFilters(newFilters);
            setCategoryChipName(""); // re-resolved below if still set
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
        },
        [],
    );

    // Keep the category chip's display name in sync whenever Advanced
    // Search sets/changes/clears filters.category_id.
    useEffect(() => {
        if (!filters.category_id) {
            setCategoryChipName("");
            return;
        }
        productService
            .getCategoryById(filters.category_id)
            .then((cat) => setCategoryChipName(cat.name))
            .catch(() => setCategoryChipName(filters.category_id || ""));
    }, [filters.category_id]);

    const handleAdd = useCallback(() => {
        setSelectedProduct(null);
        setFormMode("create");
        setFormOpen(true);
    }, []);

    // List rows are lean (no descriptions). The form saves the whole
    // product, so it must always open with the full record — otherwise
    // saving would erase the description.
    const openFormWithFullProduct = useCallback(
        async (id: string, mode: "create" | "edit") => {
            setBusy(true);
            try {
                const full = await productService.getProductById(id);
                setSelectedProduct(full);
                setFormMode(mode);
                setFormOpen(true);
            } catch {
                setError("Failed to load the product. Please try again.");
            } finally {
                setBusy(false);
            }
        },
        [],
    );

    const handleEdit = useCallback(
        (id: string) => openFormWithFullProduct(id, "edit"),
        [openFormWithFullProduct],
    );

    const handleDelete = useCallback(
        async (id: string) => {
            if (
                window.confirm("Are you sure you want to delete this product?")
            ) {
                try {
                    await productService.deleteProduct(id);
                    refreshProducts();
                } catch (err) {
                    setError("Failed to delete product");
                }
            }
        },
        [refreshProducts],
    );

    const handleClone = useCallback(
        (id: string) => openFormWithFullProduct(id, "create"),
        [openFormWithFullProduct],
    );

    // The drawer opens on the lean row straight away, then fills in the
    // description and categories once the full product arrives.
    const showInDrawer = useCallback((product: Product) => {
        setDrawerProduct(product);
        productService
            .getProductById(product.product_id)
            .then((full) =>
                setDrawerProduct((current) =>
                    current?.product_id === full.product_id ? full : current,
                ),
            )
            .catch(() => {
                // keep showing the row's data
            });
    }, []);

    const handleView = useCallback(
        (id: string) => {
            const product = products.find((p) => p.product_id === id);
            if (product) {
                showInDrawer(product);
                setDrawerOpen(true);
            }
        },
        [products, showInDrawer],
    );

    const handleOpenBarcodeDialog = useCallback(
        (id: string) => {
            const product = products.find((p) => p.product_id === id);
            if (product) {
                setBarcodeDialogProduct(product);
                setBarcodeDialogOpen(true);
            }
        },
        [products],
    );

    const handleBarcodeSaved = useCallback((updated: Product) => {
        setProducts((prev) =>
            prev.map((p) =>
                p.product_id === updated.product_id ? updated : p,
            ),
        );
    }, []);

    // A quick edit in the drawer: show the fresh product there and in its
    // table row, without reloading the list.
    const handleDrawerProductChanged = useCallback((fresh: Product) => {
        setDrawerProduct((current) => (current?.product_id === fresh.product_id ? fresh : current));
        setProducts((prev) => prev.map((p) => (p.product_id === fresh.product_id ? fresh : p)));
    }, []);

    const handleDrawerNavigate = useCallback(
        (direction: "prev" | "next") => {
            if (!drawerProduct) return;
            const idx = products.findIndex(
                (p) => p.product_id === drawerProduct.product_id,
            );
            const nextIdx = direction === "prev" ? idx - 1 : idx + 1;
            if (nextIdx >= 0 && nextIdx < products.length) {
                showInDrawer(products[nextIdx]);
            }
        },
        [drawerProduct, products, showInDrawer],
    );

    const handleFormSubmit = useCallback(
        async (product: Product) => {
            refreshProducts(); // keeps the current page, search and filters
            setFormOpen(false);
        },
        [refreshProducts],
    );

    const handleVisibilityToggleClick = useCallback(
        (productId: string, productName: string, currentValue: boolean) => {
            setVisibilityDialog({ open: true, productId, productName, currentValue });
        },
        [],
    );

    const handleVisibilityConfirm = useCallback(async () => {
        const { productId, currentValue } = visibilityDialog;
        setVisibilityDialog((d) => ({ ...d, open: false }));
        setTogglingVisibility(productId);
        try {
            await productService.updateProduct(productId, {
                is_visible: !currentValue,
            });
            setProducts((prev) =>
                prev.map((p) =>
                    p.product_id === productId
                        ? { ...p, is_visible: !currentValue }
                        : p,
                ),
            );
        } catch {
            setError("Failed to update product visibility");
        } finally {
            setTogglingVisibility(null);
        }
    }, [visibilityDialog]);

    const handleBulkVisibility = useCallback((action: "show" | "hide") => {
        setBulkDialog({ open: true, action });
    }, []);

    const handleBulkRemoveDiscount = useCallback(() => {
        setBulkDialog({ open: true, action: "remove-discount" });
    }, []);

    const handleBulkConfirm = useCallback(async () => {
        const { action } = bulkDialog;
        setBulkDialog((d) => ({ ...d, open: false }));
        setBulkLoading(true);
        try {
            const ids = Array.from(selectedRows.ids) as string[];
            if (action === "remove-discount") {
                await Promise.all(
                    ids.map((id) =>
                        productService.updateProduct(id, {
                            sale_price: null,
                            sale_starts_at: null,
                            sale_ends_at: null,
                        }),
                    ),
                );
                const idSet = new Set(ids);
                setProducts((prev) =>
                    prev.map((p) =>
                        idSet.has(p.product_id)
                            ? {
                                  ...p,
                                  sale_price: null,
                                  sale_starts_at: null,
                                  sale_ends_at: null,
                                  is_on_sale: false,
                                  discount_percent: null,
                                  effective_price: p.selling_price,
                              }
                            : p,
                    ),
                );
            } else {
                const isVisible = action === "show";
                await Promise.all(
                    ids.map((id) =>
                        productService.updateProduct(id, { is_visible: isVisible }),
                    ),
                );
                const idSet = new Set(ids);
                setProducts((prev) =>
                    prev.map((p) =>
                        idSet.has(p.product_id)
                            ? { ...p, is_visible: isVisible }
                            : p,
                    ),
                );
            }
            setSelectedRows({ type: "include", ids: new Set() });
        } catch {
            setError(
                action === "remove-discount"
                    ? "Failed to remove the discount for selected products"
                    : "Failed to update visibility for selected products",
            );
        } finally {
            setBulkLoading(false);
        }
    }, [bulkDialog, selectedRows]);

    const selectedProducts = React.useMemo(
        () => products.filter((p) => selectedRows.ids.has(p.product_id)),
        [products, selectedRows],
    );

    const computeDiscountedPrice = useCallback(
        (sellingPrice: number): number => {
            const raw =
                discountMode === "percent"
                    ? sellingPrice * (1 - discountValue / 100)
                    : sellingPrice - discountValue;
            return Math.round(Math.max(raw, 0) * 100) / 100;
        },
        [discountMode, discountValue],
    );

    const handleOpenDiscountDialog = useCallback(() => {
        setDiscountMode("percent");
        setDiscountValue(0);
        setDiscountStartsAt(null);
        setDiscountEndsAt(null);
        setDiscountDialog(true);
    }, []);

    const handleBulkApplyDiscount = useCallback(async () => {
        setDiscountDialog(false);
        setBulkLoading(true);
        try {
            const ids = Array.from(selectedRows.ids) as string[];
            const startsAtIso = discountStartsAt ? discountStartsAt.toISOString() : null;
            const endsAtIso = discountEndsAt ? discountEndsAt.toISOString() : null;
            const priceById = new Map<string, number>();
            for (const p of selectedProducts) {
                priceById.set(p.product_id, computeDiscountedPrice(p.selling_price));
            }
            await Promise.all(
                ids.map((id) => {
                    const salePrice = priceById.get(id);
                    return productService.updateProduct(id, {
                        sale_price: salePrice,
                        sale_starts_at: startsAtIso,
                        sale_ends_at: endsAtIso,
                    });
                }),
            );
            const idSet = new Set(ids);
            setProducts((prev) =>
                prev.map((p) => {
                    if (!idSet.has(p.product_id)) return p;
                    const salePrice = priceById.get(p.product_id) ?? null;
                    const isOnSale =
                        salePrice != null &&
                        salePrice < p.selling_price &&
                        (!startsAtIso || new Date(startsAtIso) <= new Date()) &&
                        (!endsAtIso || new Date(endsAtIso) >= new Date());
                    return {
                        ...p,
                        sale_price: salePrice,
                        sale_starts_at: startsAtIso,
                        sale_ends_at: endsAtIso,
                        is_on_sale: isOnSale,
                        effective_price: isOnSale ? salePrice! : p.selling_price,
                        discount_percent:
                            isOnSale && salePrice != null
                                ? Math.round((1 - salePrice / p.selling_price) * 100)
                                : null,
                    };
                }),
            );
            setSelectedRows({ type: "include", ids: new Set() });
        } catch {
            setError("Failed to apply the discount for selected products");
        } finally {
            setBulkLoading(false);
        }
    }, [selectedRows, selectedProducts, discountStartsAt, discountEndsAt, computeDiscountedPrice]);

    // Memoized so the grid keeps its cells between unrelated re-renders
    // (drawer, dialogs, selection) instead of rebuilding all 50 rows.
    const columns: GridColDef[] = React.useMemo(() => [
        {
            field: "thumbnail",
            headerName: "Image",
            width: 80,
            sortable: false,
            renderCell: (params) => {
                const images = (params.row.images || []) as ProductImage[];
                const defaultImage =
                    images.find((img: ProductImage) => img.is_default) ||
                    images[0];

                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            height: "100%",
                            py: 0.5,
                        }}
                    >
                        {defaultImage ? (
                            <ProductImageButton
                                src={defaultImage.thumbnail_url}
                                alt={params.row.name}
                                size={50}
                                count={images.length}
                                onClick={() => openImages(params.row)}
                            />
                        ) : (
                            <Avatar
                                variant="rounded"
                                sx={{
                                    width: 50,
                                    height: 50,
                                    bgcolor: "grey.200",
                                }}
                            >
                                <ImageIcon sx={{ color: "grey.400" }} />
                            </Avatar>
                        )}
                    </Box>
                );
            },
        },
        {
            field: "name",
            headerName: "Product Name",
            flex: 1,
            minWidth: 200,
            renderCell: (params) => (
                <Typography
                    variant="body2"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleView(params.row.product_id);
                    }}
                    sx={{
                        cursor: "pointer",
                        "&:hover": {
                            color: "primary.main",
                            textDecoration: "underline",
                        },
                    }}
                >
                    {params.value}
                </Typography>
            ),
        },
        {
            field: "available_stock",
            headerName: "Available Stock",
            width: 160,
            renderCell: (params) => {
                const stockObj = params.row?.stock;
                const qty: number = Number(stockObj?.quantity ?? 0);
                const reserved: number = Number(
                    stockObj?.reserved_quantity ?? 0,
                );
                // Prefer direct available if quantity was synthesized, else compute
                const available = Math.max(qty - reserved, 0);
                return (
                    <Tooltip
                        title={`Current: ${qty} | Reserved: ${reserved}`}
                        arrow
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                height: "100%",
                            }}
                        >
                            <Typography variant="body2" fontWeight="medium">
                                {available}
                            </Typography>
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: "category",
            headerName: "Category",
            width: 150,
            renderCell: (params) => (
                <Chip
                    icon={<Category />}
                    label={params.row.category?.name || "No Category"}
                    size="small"
                    variant="outlined"
                />
            ),
        },
        {
            field: "brand",
            headerName: "Brand",
            width: 150,
            renderCell: (params) => (
                <Chip
                    icon={<Business />}
                    label={params.row.brand?.name || "No Brand"}
                    size="small"
                    variant="outlined"
                />
            ),
        },
        {
            field: "selling_price",
            headerName: "Selling Price",
            width: 140,
            renderCell: (params) => {
                const product = params.row as Product;
                if (product.is_on_sale) {
                    return (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                height: "100%",
                            }}
                        >
                            <Typography
                                variant="caption"
                                color="text.disabled"
                                sx={{ textDecoration: "line-through" }}
                            >
                                {formatCurrencyBDT(Number(params.value) || 0)}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Typography variant="body2" fontWeight="medium" color="error.main">
                                    {formatCurrencyBDT(Number(product.effective_price) || 0)}
                                </Typography>
                                <ComboChip product={product} />
                            </Box>
                        </Box>
                    );
                }
                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            height: "100%",
                        }}
                    >
                        <Typography variant="body2" fontWeight="medium">
                            {formatCurrencyBDT(Number(params.value) || 0)}
                        </Typography>
                        <ComboChip product={product} />
                    </Box>
                );
            },
        },
        {
            field: "cost_price",
            headerName: "Buying Price",
            width: 120,
            renderCell: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        height: "100%",
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        {formatCurrencyBDT(Number(params.value) || 0)}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "is_active",
            headerName: "Status",
            width: 100,
            renderCell: (params) => (
                <Chip
                    label={params.value ? "Active" : "Inactive"}
                    color={params.value ? "success" : "default"}
                    size="small"
                />
            ),
        },
        {
            field: "is_visible",
            headerName: "Website",
            width: 110,
            sortable: false,
            renderCell: (params) => {
                const isVisible = params.value !== false;
                const isToggling = togglingVisibility === params.row.product_id;
                return (
                    <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                        <Tooltip title={isVisible ? "Click to hide from website" : "Click to show on website"}>
                            <span>
                                <Switch
                                    checked={isVisible}
                                    size="small"
                                    color="primary"
                                    disabled={isToggling}
                                    onChange={() =>
                                        handleVisibilityToggleClick(
                                            params.row.product_id,
                                            params.row.name,
                                            isVisible,
                                        )
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </span>
                        </Tooltip>
                    </Box>
                );
            },
        },
        {
            field: "create_time",
            headerName: "Created",
            width: 120,
            type: "date",
            valueGetter: (value: string) => new Date(value),
        },
        {
            field: "clone_action",
            headerName: "Clone",
            width: 80,
            sortable: false,
            renderCell: (params) => (
                <Tooltip title="Clone this product">
                    <Button
                        size="small"
                        onClick={() => handleClone(params.row.product_id)}
                        sx={{ minWidth: "auto" }}
                    >
                        <ContentCopy fontSize="small" />
                    </Button>
                </Tooltip>
            ),
        },
        {
            field: "barcode_action",
            headerName: "Barcode",
            width: 90,
            sortable: false,
            renderCell: (params) => (
                <Tooltip
                    title={
                        params.row.barcode
                            ? "Check / update barcode"
                            : "Attach barcode"
                    }
                >
                    <Button
                        size="small"
                        color={params.row.barcode ? "inherit" : "warning"}
                        onClick={() =>
                            handleOpenBarcodeDialog(params.row.product_id)
                        }
                        sx={{ minWidth: "auto" }}
                    >
                        <QrCodeScannerIcon fontSize="small" />
                    </Button>
                </Tooltip>
            ),
        },
    ], [
        handleClone,
        handleOpenBarcodeDialog,
        handleView,
        handleVisibilityToggleClick,
        togglingVisibility,
        openImages,
    ]);

    // Filter columns based on visibility settings, and drop write-only
    // action columns entirely for a read-only (Investor) login.
    const visibleColumns = React.useMemo(() => {
        const WRITE_ONLY_COLUMNS = new Set(["clone_action", "barcode_action"]);
        return columns.filter((column) => {
            if (!canWrite && WRITE_ONLY_COLUMNS.has(column.field)) return false;
            // All columns should be visible by default unless explicitly hidden
            return columnVisibility[column.field] !== false;
        });
    }, [columns, canWrite, columnVisibility]);

    const rows = React.useMemo(
        () => products.map((product) => ({ id: product.product_id, ...product })),
        [products],
    );

    return (
        <Box>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 3,
                    gap: 2,
                    flexWrap: "wrap",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{ fontSize: { xs: "1.35rem", sm: "2.125rem" } }}
                    >
                        Products ({totalRows})
                    </Typography>
                    {filters.category_id && categoryChipName && (
                        <Chip
                            icon={<FilterListIcon fontSize="small" />}
                            label={`Category: ${categoryChipName}`}
                            color="primary"
                            variant="outlined"
                            size="small"
                            onDelete={() => {
                                const updated = {
                                    ...filters,
                                    category_id: undefined,
                                };
                                setFilters(updated);
                                setSearchParams({});
                                setPaginationModel((prev) => ({ ...prev, page: 0 }));
                            }}
                            deleteIcon={<ClearIcon fontSize="small" />}
                        />
                    )}
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                        width: { xs: "100%", sm: "auto" },
                    }}
                >
                    {/* Search Input */}
                    <ProductSearchBox
                        value={searchTerm}
                        onSearch={handleSearch}
                        searching={loading && !!searchTerm}
                        placeholder={
                            isMobile
                                ? "Search by name, SKU, or barcode..."
                                : "Search products (name, SKU, barcode, category, brand)..."
                        }
                        onScanClick={() => setBarcodeScannerOpen(true)}
                        autoFocus
                    />
                    <Tooltip title="Advanced Search">
                        <Badge
                            badgeContent={countActiveProductFilters(filters)}
                            color="primary"
                        >
                            <Button
                                variant="outlined"
                                startIcon={<FilterListIcon />}
                                onClick={() => setAdvancedSearchOpen(true)}
                                size="small"
                            >
                                {isMobile ? "Filters" : "Advanced Search"}
                            </Button>
                        </Badge>
                    </Tooltip>
                    <Tooltip title={exporting ? "Exporting..." : "Export CSV"}>
                        <Button
                            variant="outlined"
                            startIcon={<FileDownload />}
                            onClick={handleExport}
                            disabled={exporting}
                            size="small"
                        >
                            {isMobile
                                ? exporting
                                    ? "..."
                                    : "CSV"
                                : exporting
                                  ? "Exporting..."
                                  : "Export CSV"}
                        </Button>
                    </Tooltip>
                    {canWrite && (
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleAdd}
                            size="small"
                        >
                            {isMobile ? "Add" : "Add Product"}
                        </Button>
                    )}
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Mobile Card View / Desktop DataGrid */}
            {isMobile ? (
                <Box sx={{ mt: 1 }}>
                    {loading ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                py: 6,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : products.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 6 }}>
                            <Typography color="text.secondary">
                                No products found.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {products.map((product) => {
                                const images = (product.images ||
                                    []) as ProductImage[];
                                const defaultImage =
                                    images.find((img) => img.is_default) ||
                                    images[0];
                                const qty = Number(
                                    product.stock?.quantity ?? 0,
                                );
                                const reserved = Number(
                                    product.stock?.reserved_quantity ?? 0,
                                );
                                const available = Math.max(qty - reserved, 0);

                                return (
                                    <Card
                                        key={product.product_id}
                                        variant="outlined"
                                        sx={{ mb: 1.5, borderRadius: 2 }}
                                    >
                                        <CardContent
                                            sx={{ pb: "8px !important" }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    gap: 1.5,
                                                    alignItems: "flex-start",
                                                }}
                                            >
                                                {defaultImage ? (
                                                    <ProductImageButton
                                                        src={
                                                            defaultImage.thumbnail_url
                                                        }
                                                        alt={product.name}
                                                        size={56}
                                                        count={
                                                            product.images?.length ?? 0
                                                        }
                                                        onClick={() =>
                                                            openImages(product)
                                                        }
                                                    />
                                                ) : (
                                                    <Avatar
                                                        variant="rounded"
                                                        sx={{
                                                            width: 56,
                                                            height: 56,
                                                            bgcolor: "grey.200",
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        <ImageIcon
                                                            sx={{
                                                                color: "grey.400",
                                                            }}
                                                        />
                                                    </Avatar>
                                                )}

                                                <Box
                                                    sx={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            justifyContent:
                                                                "space-between",
                                                            alignItems:
                                                                "flex-start",
                                                            mb: 0.5,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body1"
                                                            fontWeight="bold"
                                                            sx={{
                                                                flex: 1,
                                                                mr: 1,
                                                                overflow:
                                                                    "hidden",
                                                                textOverflow:
                                                                    "ellipsis",
                                                                whiteSpace:
                                                                    "nowrap",
                                                            }}
                                                        >
                                                            {product.name}
                                                        </Typography>
                                                        <Chip
                                                            label={
                                                                product.is_active
                                                                    ? "Active"
                                                                    : "Inactive"
                                                            }
                                                            color={
                                                                product.is_active
                                                                    ? "success"
                                                                    : "default"
                                                            }
                                                            size="small"
                                                            sx={{
                                                                flexShrink: 0,
                                                                height: 20,
                                                                fontSize:
                                                                    "0.65rem",
                                                            }}
                                                        />
                                                    </Box>

                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            justifyContent:
                                                                "space-between",
                                                            alignItems:
                                                                "center",
                                                            mb: 0.5,
                                                        }}
                                                    >
                                                        {product.is_on_sale ? (
                                                            <Box>
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.disabled"
                                                                    sx={{ textDecoration: "line-through" }}
                                                                    display="block"
                                                                >
                                                                    {formatCurrencyBDT(
                                                                        product.selling_price,
                                                                    )}
                                                                </Typography>
                                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                                    <Typography
                                                                        variant="body1"
                                                                        color="error.main"
                                                                        fontWeight="bold"
                                                                    >
                                                                        {formatCurrencyBDT(
                                                                            product.effective_price ?? product.selling_price,
                                                                        )}
                                                                    </Typography>
                                                                    <ComboChip product={product} />
                                                                </Box>
                                                            </Box>
                                                        ) : (
                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                                <Typography
                                                                    variant="body1"
                                                                    color="primary.main"
                                                                    fontWeight="bold"
                                                                >
                                                                    {formatCurrencyBDT(
                                                                        product.selling_price,
                                                                    )}
                                                                </Typography>
                                                                <ComboChip product={product} />
                                                            </Box>
                                                        )}
                                                        <Tooltip
                                                            title={`Total: ${qty} | Reserved: ${reserved}`}
                                                            arrow
                                                        >
                                                            <Typography
                                                                variant="caption"
                                                                color={
                                                                    available <=
                                                                    5
                                                                        ? "error"
                                                                        : "text.secondary"
                                                                }
                                                            >
                                                                Stock:{" "}
                                                                {available}
                                                            </Typography>
                                                        </Tooltip>
                                                    </Box>

                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            gap: 0.5,
                                                            flexWrap: "wrap",
                                                        }}
                                                    >
                                                        {product.category
                                                            ?.name && (
                                                            <Chip
                                                                icon={
                                                                    <Category />
                                                                }
                                                                label={
                                                                    product
                                                                        .category
                                                                        .name
                                                                }
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{
                                                                    height: 20,
                                                                    fontSize:
                                                                        "0.65rem",
                                                                }}
                                                            />
                                                        )}
                                                        {product.brand
                                                            ?.name && (
                                                            <Chip
                                                                icon={
                                                                    <Business />
                                                                }
                                                                label={
                                                                    product
                                                                        .brand
                                                                        .name
                                                                }
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{
                                                                    height: 20,
                                                                    fontSize:
                                                                        "0.65rem",
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </CardContent>

                                        <Divider />

                                        <CardActions
                                            sx={{
                                                px: 1,
                                                py: 0.5,
                                                justifyContent:
                                                    "space-between",
                                            }}
                                        >
                                            <Tooltip title="View details">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        handleView(
                                                            product.product_id,
                                                        )
                                                    }
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {canWrite && (
                                                <Tooltip
                                                    title={
                                                        product.barcode
                                                            ? "Check / update barcode"
                                                            : "Attach barcode"
                                                    }
                                                >
                                                    <IconButton
                                                        size="small"
                                                        color={
                                                            product.barcode
                                                                ? "default"
                                                                : "warning"
                                                        }
                                                        onClick={() =>
                                                            handleOpenBarcodeDialog(
                                                                product.product_id,
                                                            )
                                                        }
                                                    >
                                                        <QrCodeScannerIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {canWrite && (
                                                <Tooltip title="Edit product">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            handleEdit(
                                                                product.product_id,
                                                            )
                                                        }
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {canWrite && (
                                                <Tooltip title="Clone this product">
                                                    <IconButton
                                                        size="small"
                                                        color="secondary"
                                                        onClick={() =>
                                                            handleClone(
                                                                product.product_id,
                                                            )
                                                        }
                                                    >
                                                        <ContentCopy fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {canWrite && (
                                                <Tooltip title="Delete product">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() =>
                                                            handleDelete(
                                                                product.product_id,
                                                            )
                                                        }
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </CardActions>
                                    </Card>
                                );
                            })}

                            {/* Mobile Pagination */}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mt: 1,
                                    py: 1,
                                }}
                            >
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={paginationModel.page === 0}
                                    onClick={() =>
                                        handlePaginationModelChange({
                                            ...paginationModel,
                                            page: paginationModel.page - 1,
                                        })
                                    }
                                >
                                    ← Prev
                                </Button>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {paginationModel.page *
                                        paginationModel.pageSize +
                                        1}
                                    –
                                    {Math.min(
                                        (paginationModel.page + 1) *
                                            paginationModel.pageSize,
                                        totalRows,
                                    )}{" "}
                                    of {totalRows}
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={
                                        (paginationModel.page + 1) *
                                            paginationModel.pageSize >=
                                        totalRows
                                    }
                                    onClick={() =>
                                        handlePaginationModelChange({
                                            ...paginationModel,
                                            page: paginationModel.page + 1,
                                        })
                                    }
                                >
                                    Next →
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            ) : (
                <>
                    {/* Bulk action toolbar — only visible when rows are selected */}
                    {canWrite && selectedRows.ids.size > 0 && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 1.5,
                                mb: 1,
                                px: 2,
                                py: 1,
                                bgcolor: "action.selected",
                                borderRadius: 1,
                            }}
                        >
                            <Typography variant="body2" sx={{ mr: 1 }}>
                                {selectedRows.ids.size} product{selectedRows.ids.size !== 1 ? "s" : ""} selected
                            </Typography>
                            <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                startIcon={<VisibilityIcon />}
                                disabled={bulkLoading}
                                onClick={() => handleBulkVisibility("show")}
                            >
                                Show on Website
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={<VisibilityOffIcon />}
                                disabled={bulkLoading}
                                onClick={() => handleBulkVisibility("hide")}
                            >
                                Hide from Website
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<SaleIcon />}
                                disabled={bulkLoading}
                                onClick={handleOpenDiscountDialog}
                            >
                                Apply Discount
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={<CampaignIcon />}
                                disabled={bulkLoading}
                                onClick={() => setCampaignDialog(true)}
                            >
                                Add to Campaign
                            </Button>
                            {selectedProducts.some((p) => p.sale_price != null) && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="inherit"
                                    disabled={bulkLoading}
                                    onClick={handleBulkRemoveDiscount}
                                >
                                    Remove Discount
                                </Button>
                            )}
                            {bulkLoading && <CircularProgress size={18} />}
                        </Box>
                    )}

                    {/* Refreshing keeps the current rows on screen under a thin
                        bar — the grid's own loading overlay redraws every cell,
                        twice per search. It's still used for the first load. */}
                    <Box sx={{ height: 4 }}>
                        {loading && products.length > 0 && <LinearProgress />}
                    </Box>
                    <ServerSideDataTable
                        rows={rows}
                        columns={visibleColumns}
                        loading={loading && products.length === 0}
                        onEdit={canWrite ? handleEdit : undefined}
                        onDelete={canWrite ? handleDelete : undefined}
                        onView={handleView}
                        totalRows={totalRows}
                        paginationModel={paginationModel}
                        onPaginationModelChange={handlePaginationModelChange}
                        checkboxSelection={canWrite}
                        rowSelectionModel={selectedRows}
                        onRowSelectionModelChange={setSelectedRows}
                    />
                </>
            )}

            <Backdrop
                open={busy}
                sx={{ zIndex: (t) => t.zIndex.drawer + 1, color: "common.white" }}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            <ProductForm
                open={formOpen}
                onClose={() => setFormOpen(false)}
                onSubmit={handleFormSubmit}
                product={selectedProduct}
                mode={formMode}
            />

            <ProductAdvancedSearch
                open={advancedSearchOpen}
                onClose={() => setAdvancedSearchOpen(false)}
                onSearch={handleAdvancedSearch}
                initialParams={filters}
            />

            <ProductImageViewer
                target={imageViewer}
                onClose={() => setImageViewer(null)}
            />

            <ProductDetailDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                product={drawerProduct}
                onProductChanged={canWrite ? handleDrawerProductChanged : undefined}
                onEdit={canWrite ? (id) => {
                    setDrawerOpen(false);
                    handleEdit(id);
                } : undefined}
                onDelete={canWrite ? (id) => {
                    setDrawerOpen(false);
                    handleDelete(id);
                } : undefined}
                onClone={canWrite ? (id) => {
                    setDrawerOpen(false);
                    handleClone(id);
                } : undefined}
                onNavigate={handleDrawerNavigate}
                hasPrev={
                    drawerProduct
                        ? products.findIndex(
                              (p) => p.product_id === drawerProduct.product_id,
                          ) > 0
                        : false
                }
                hasNext={
                    drawerProduct
                        ? products.findIndex(
                              (p) => p.product_id === drawerProduct.product_id,
                          ) <
                          products.length - 1
                        : false
                }
                currentIndex={
                    drawerProduct
                        ? products.findIndex(
                              (p) => p.product_id === drawerProduct.product_id,
                          )
                        : undefined
                }
                totalCount={products.length}
            />

            <QuickBarcodeDialog
                open={barcodeDialogOpen}
                onClose={() => setBarcodeDialogOpen(false)}
                product={barcodeDialogProduct}
                onSaved={handleBarcodeSaved}
            />

            {/* Inline visibility confirmation dialog */}
            <Dialog
                open={visibilityDialog.open}
                onClose={() => setVisibilityDialog((d) => ({ ...d, open: false }))}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    {visibilityDialog.currentValue
                        ? "Hide Product from Website"
                        : "Show Product on Website"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {visibilityDialog.currentValue
                            ? `"${visibilityDialog.productName}" will be hidden from the website immediately.`
                            : `"${visibilityDialog.productName}" will become visible on the website immediately.`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() =>
                            setVisibilityDialog((d) => ({ ...d, open: false }))
                        }
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color={visibilityDialog.currentValue ? "warning" : "primary"}
                        onClick={handleVisibilityConfirm}
                    >
                        {visibilityDialog.currentValue ? "Hide" : "Show"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk visibility confirmation dialog */}
            <Dialog
                open={bulkDialog.open}
                onClose={() => setBulkDialog((d) => ({ ...d, open: false }))}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    {bulkDialog.action === "show"
                        ? "Show Selected Products on Website"
                        : bulkDialog.action === "hide"
                          ? "Hide Selected Products from Website"
                          : "Remove Discount from Selected Products"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {bulkDialog.action === "show"
                            ? `${selectedRows.ids.size} product${selectedRows.ids.size !== 1 ? "s" : ""} will become visible on the website.`
                            : bulkDialog.action === "hide"
                              ? `${selectedRows.ids.size} product${selectedRows.ids.size !== 1 ? "s" : ""} will be hidden from the website.`
                              : `${selectedRows.ids.size} product${selectedRows.ids.size !== 1 ? "s" : ""} will revert to their regular selling price.`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() =>
                            setBulkDialog((d) => ({ ...d, open: false }))
                        }
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color={
                            bulkDialog.action === "show"
                                ? "primary"
                                : bulkDialog.action === "hide"
                                  ? "warning"
                                  : "error"
                        }
                        onClick={handleBulkConfirm}
                    >
                        {bulkDialog.action === "show"
                            ? "Show All"
                            : bulkDialog.action === "hide"
                              ? "Hide All"
                              : "Remove Discount"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk "Apply Discount" dialog — percentage or flat amount off,
                computed client-side per product into each one's own
                absolute sale_price; nothing named discount_type/value is
                ever sent to the API. */}
            <Dialog
                open={discountDialog}
                onClose={() => setDiscountDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Apply Discount</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                        <ToggleButtonGroup
                            value={discountMode}
                            exclusive
                            size="small"
                            onChange={(_, value) => value && setDiscountMode(value)}
                        >
                            <ToggleButton value="percent">Percent off</ToggleButton>
                            <ToggleButton value="flat">Flat amount off</ToggleButton>
                        </ToggleButtonGroup>
                        <TextField
                            label={discountMode === "percent" ? "Discount (%)" : "Discount (৳)"}
                            type="number"
                            size="small"
                            fullWidth
                            value={discountValue || ""}
                            onChange={(e) =>
                                setDiscountValue(parseFloat(e.target.value) || 0)
                            }
                            inputProps={{
                                min: 0,
                                max: discountMode === "percent" ? 100 : undefined,
                                step: discountMode === "percent" ? 1 : 0.01,
                            }}
                        />
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <Box sx={{ display: "flex", gap: 2 }}>
                                <DateTimePicker
                                    label="Starts (optional)"
                                    value={discountStartsAt}
                                    onChange={setDiscountStartsAt}
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            fullWidth: true,
                                            helperText: "Blank starts immediately",
                                        },
                                    }}
                                />
                                <DateTimePicker
                                    label="Ends (optional)"
                                    value={discountEndsAt}
                                    onChange={setDiscountEndsAt}
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            fullWidth: true,
                                            helperText: "Blank runs indefinitely",
                                        },
                                    }}
                                />
                            </Box>
                        </LocalizationProvider>

                        {discountValue > 0 && (
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Product</TableCell>
                                            <TableCell align="right">Current</TableCell>
                                            <TableCell align="right">New Sale Price</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {selectedProducts.map((p) => (
                                            <TableRow key={p.product_id}>
                                                <TableCell>{p.name}</TableCell>
                                                <TableCell align="right">
                                                    {formatCurrencyBDT(p.selling_price)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatCurrencyBDT(
                                                        computeDiscountedPrice(p.selling_price),
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDiscountDialog(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        disabled={discountValue <= 0}
                        onClick={handleBulkApplyDiscount}
                    >
                        Apply to {selectedRows.ids.size} Product
                        {selectedRows.ids.size !== 1 ? "s" : ""}
                    </Button>
                </DialogActions>
            </Dialog>
            <AddToCampaignDialog
                open={campaignDialog}
                productIds={Array.from(selectedRows.ids) as string[]}
                onClose={() => setCampaignDialog(false)}
                onAdded={refreshProducts}
            />
            <BarcodeScannerDialog
                open={barcodeScannerOpen}
                onClose={() => setBarcodeScannerOpen(false)}
                onScan={(barcode) => handleSearch(barcode.trim())}
                title="Scan Product Barcode"
            />
        </Box>
    );
};
